import { Fragment, lazy, Suspense, useRef, useState } from "react";
import { Outlet } from "react-router-dom";

import Loader from "components/ui/loader";
import ErrorMessage from "components/ui/errorMessage";
import BlockedMessage from "components/ui/blockedMessage";
import Maintenance from "components/ui/maintenance";
import MobileHeader from "components/mobileHeader";
import ConditionalStyles from "components/ui/conditionalStyles";

import useAppDispatch from "hooks/store/useAppDispatch";
import useAppSelector from "hooks/store/useAppSelector";
import useGlobalVariables from "hooks/useGlobalVariables";
import useAsyncEffect from "hooks/useAsyncEffect";

import { authenticateThunk, createSessionThunk, createStreamAccessThunk, sessionDetailsThunk } from "store/slices/auth/thunks";
import { setSessionAction, setSessionFailedAction, setSessionIdAction, setTranslationsLoadedAction } from "store/slices/auth/actions";
import {
	selectSessionLoaded,
	selectSessionFailed,
	selectSessionMaintenanceMode,
	selectTranslationsLoaded
} from "store/slices/auth/selectors";
import { selectBlockedMessage, selectErrorMessage } from "store/slices/common/selectors";

import { initTranslations } from "translations/config";

import { showError } from "utils/message";
import { buildPathToStaticFolderOfCDN, getUrlVars } from "utils/common";
import { appendCss, buildCssUrl } from "utils/css";
import Paths from "constants/path.constants";
import { GAME_ACTIVITY_STATE } from "constants/game.constants";
import ThemeContext from "./components/theme-context";
import { ERROR_MESSAGES, ERROR_RESOURCES, ERROR_REASONS } from "helpers/responseManager/constants";
const LightModeStyles = lazy(() => import("./components/light-mode-styles/light-version-1"));
const DarkModeVersion1Styles = lazy(() => import("./components/dark-mode-styles/dark-version-1"));
const DarkModeVersion2Styles = lazy(() => import("./components/dark-mode-styles/dark-version-2"));


const RootLayout = () => {
	const sessionLoaded = useAppSelector(selectSessionLoaded);
	const sessionFailed = useAppSelector(selectSessionFailed);
	const maintenanceMode = useAppSelector(selectSessionMaintenanceMode);
	const translationsLoaded = useAppSelector(selectTranslationsLoaded);
	const blockedMessage = useAppSelector(selectBlockedMessage);
	const errorMessage = useAppSelector(selectErrorMessage);
	const dispatch = useAppDispatch();
	const globalVariables = useGlobalVariables();

	const [modeStyles, setModeStyles] = useState(null);
	const variableRef = useRef();
	variableRef.current = globalVariables;
	const { isMobile, isBuilderIntegration } = globalVariables;

	useAsyncEffect(async () => {
		try {
			const urlVars = getUrlVars();

			/** @type {Boolean} */
			let customView = false;
			/** @type {HTMLLinkElement} */
			let customCssLinkTag = null;
			const onModeChange = (e) => {
				if (!e.data || !e.data.type || e.data.type !== "theme-change") {
					return
				}
				const { data: mode } = e.data;
				if (!["light", "dark", null].includes(mode)) {
					return
				}
				const modeVersion = mode === null ? null : (Number(urlVars.modeVersion) || 1)
				if (import.meta.env.DEV) {
					const selectorsForDisable = [];
					const selectorsForEnable = [];
					switch (mode) {
						case "light":
							selectorsForDisable.push('style[data-vite-dev-id*="dark-mode-styles"]')
							selectorsForDisable.push('style[data-vite-dev-id*="light-mode-styles"]')
							selectorsForEnable.push(`style[data-vite-dev-id*="light-mode-styles/light-${modeVersion}.scss"]`)
							break;
						case "dark":
							selectorsForDisable.push(`style[data-vite-dev-id*="light-mode-styles"]`)
							selectorsForDisable.push('style[data-vite-dev-id*="dark-mode-styles"]')
							selectorsForEnable.push(`style[data-vite-dev-id*="dark-mode-styles/dark-${modeVersion}.scss"]`)
							break;
						default:
							selectorsForDisable.push('style[data-vite-dev-id*="dark-mode-styles"]')
							selectorsForDisable.push('style[data-vite-dev-id*="light-mode-styles"]')
							break;
					}

					selectorsForDisable.forEach(selector => {
						const styles = document.querySelectorAll(selector);
						styles.forEach(styleTag => {
							styleTag.media = "not all";
						})
					})

					selectorsForEnable.forEach(selector => {
						const styles = document.querySelectorAll(selector);
						styles.forEach(styleTag => {
							styleTag.media = "all";
						})
					})
				}

				if (import.meta.env.PROD) {
					const selectorsForDisable = [];
					const selectorsForEnable = [];
					switch (mode) {
						case "light":
							selectorsForDisable.push('link[href*="dark-version-1-"]')
							selectorsForDisable.push('link[href*="dark-version-2-"]')
							selectorsForEnable.push('link[href*="light-version-1-"]')
							break;
						case "dark":
							selectorsForDisable.push('link[href*="light-version-1-"]')
							selectorsForEnable.push('link[href*="dark-version-1-"]')
							selectorsForEnable.push('link[href*="dark-version-2-"]')
							break;
						default:
							selectorsForDisable.push('link[href*="dark-version-1-"]')
							selectorsForDisable.push('link[href*="dark-version-2-"]')
							selectorsForDisable.push('link[href*="light-version-1-"]')
							break;
					}

					selectorsForDisable.forEach(selector => {
						const styles = document.querySelectorAll(selector);
						styles
							.forEach(linkTag => {
								if (linkTag.href.includes(".css")) {
									linkTag.media = "not all";
								}
							})
					})

					selectorsForEnable.forEach(selector => {
						const styles = document.querySelectorAll(selector);
						styles
							.forEach(linkTag => {
								if (linkTag.href.includes(".css")) {
									linkTag.media = "all";
								}
							})
					})
				}

				customView = true;
				document.documentElement.setAttribute("mode", mode)
				document.documentElement.setAttribute("mode-version", modeVersion)
				const postfix = `-${modeVersion}`;
				const linkHref = buildPathToStaticFolderOfCDN(`${mode}${postfix}.css`)
				if (customCssLinkTag) {
					customCssLinkTag.remove()
				}
				customCssLinkTag = appendCss(linkHref);
				setModeStyles({ mode: mode, version: modeVersion });
			}

			if (["light", "dark"].includes(urlVars.mode)) {
				customView = true;
				document.documentElement.setAttribute("mode", urlVars.mode)
				document.documentElement.setAttribute("mode-version", urlVars.modeVersion || 1)
			} else {
				document.documentElement.removeAttribute("mode")
				document.documentElement.removeAttribute("mode-version")
			}
			window.addEventListener("message", onModeChange, false);

			const isStream = window.location.pathname === Paths.STREAM;

			if (isStream) {
				const guid = urlVars["guid"];
				const gameId = urlVars["gameId"];

				if (!guid || !gameId) {
					initTranslations(null, "en", () => {
						dispatch(setSessionFailedAction(true));
						showError({
							message: guid ? ERROR_MESSAGES.GAME_NOT_FOUND : ERROR_MESSAGES.GUID_NOT_FOUND
						});
					});
				} else {
					await dispatch(createStreamAccessThunk(guid)).unwrap();
					dispatch(setSessionAction({ games: [{ id: gameId }] }))
					// TODO: Need change after refactor,
					// TODO: without translationsLoaded router doesn't render Outlet
					dispatch(setTranslationsLoadedAction(true))
					return
				}
			}

			let sessionId = urlVars.sessionId;
			if (!sessionId && !isStream) {
				const data = await dispatch(createSessionThunk()).unwrap();
				sessionId = data?.session.sessionId;
			} else if (!customView) {
				const cssFilePath = urlVars["cssFilePath"];
				if (cssFilePath) {
					let url = decodeURIComponent(cssFilePath);
					appendCss(url);
				}
			}

			dispatch(setSessionIdAction(sessionId));
			await dispatch(authenticateThunk(sessionId)).unwrap();

			const { data: session } = await dispatch(sessionDetailsThunk(globalVariables)).unwrap();

			/**Append css */
			const cssId = session?.cssId;
			if (!customView && !urlVars["cssFilePath"] && cssId && session.projectId && session.partnerId) {
				appendCss(buildCssUrl(cssId, session.partnerId, session.projectId));
			}

			if (customView) {
				const postfix = `-${urlVars.modeVersion || 1}`;
				customCssLinkTag = appendCss(buildPathToStaticFolderOfCDN(`${urlVars.mode}${postfix}.css`));
				setModeStyles({ mode: urlVars.mode, version: Number(urlVars.modeVersion) || 1 });
			}

			const onTranslationSuccess = () => {
				const desktopEnabled = session?.securitySettings?.desktop;
				const iosEnabled = session?.securitySettings?.ios;
				const androidEnabled = session?.securitySettings?.android;
				const activeGamesCount = (session?.games ?? []).filter((g) => g.state !== GAME_ACTIVITY_STATE.INACTIVE).length;

				if ((!variableRef.current.isMobile && !desktopEnabled) || (variableRef.current.isIOS && !iosEnabled) || (variableRef.current.isAndroid && !androidEnabled)) {
					dispatch(setSessionFailedAction(true));
					showError({
						message: ERROR_MESSAGES.BLOCKED,
						status: ERROR_REASONS.NOT_ALLOWED,
						resource: ERROR_RESOURCES.PLAYER_DEVICE
					});
				} else if (activeGamesCount === 0) {
					dispatch(setSessionFailedAction(true));
					showError({
						message: ERROR_MESSAGES.INVALID_PLAYED_DATA
					});
				}
			};

			initTranslations(session.languageFilePath, session.languageId, onTranslationSuccess);

			if (customView) {
				return () => {
					window.removeEventListener("message", onModeChange)
				}
			}

		} catch (error) {
			await new Promise(res => {
				initTranslations(null, "en", res);
			})
			dispatch(setSessionFailedAction(true));

			const hasStatus = Object.prototype.hasOwnProperty.call(error, "status")
			const hasResource = Object.prototype.hasOwnProperty.call(error, "resource")
			const hasMessage = Object.prototype.hasOwnProperty.call(error, "message")

			if (hasStatus && hasResource && hasMessage) {
				showError({
					message: error.message,
					resource: error.resource,
					status: error.status
				});
			}
			console.log(error);
		}
	}, []);

	const renderColorModeStyles = () => {
		if (!modeStyles) {
			return null
		}

		switch (modeStyles.mode) {
			case "light":
				return (
					<Suspense fallback={null}>
						<LightModeStyles />
					</Suspense>
				);
			case "dark":
				return (
					<Suspense fallback={null}>
						{
							modeStyles.version === 2
								? <DarkModeVersion2Styles />
								: <DarkModeVersion1Styles />
						}
					</Suspense>
				);
			default:
				break;
		}

		return null
	}

	switch (true) {
		case sessionFailed && translationsLoaded:
			return (
				<ThemeContext.Provider value={modeStyles}>
					<ConditionalStyles />
					{blockedMessage ? <BlockedMessage title="common.gamesAreBlocked" message={blockedMessage} /> : null}
					{errorMessage ? <ErrorMessage /> : null}
					{maintenanceMode ? (
						<Fragment>
							{isMobile && !isBuilderIntegration ? <MobileHeader /> : null}
							<Maintenance />
						</Fragment>
					) : null}
				</ThemeContext.Provider>
			);
		case !sessionLoaded:
			return (
				<ThemeContext.Provider value={modeStyles}>
					{errorMessage ? <ErrorMessage /> : null}
					<Loader full={true} />
				</ThemeContext.Provider>
			);
		case sessionLoaded && !sessionFailed && translationsLoaded:
			return (
				<ThemeContext.Provider value={modeStyles}>
					<ConditionalStyles />
					{errorMessage ? <ErrorMessage /> : null}
					<Outlet />
					{renderColorModeStyles()}
				</ThemeContext.Provider>
			);
		default:
			return <Fragment />;
	}
};

export default RootLayout;
